import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '@/config/auth';
import { config } from '@/services';

/**
 * MSAL error type for TypeScript
 */
type MsalError = {
    errorCode: string;
    errorMessage?: string;
};

/**
 * Check if an MSAL error code is recoverable through token refresh or interactive auth
 */
const isRecoverableError = (errorCode: string): boolean => {
    return ['interaction_required', 'consent_required', 'login_required'].includes(errorCode);
};

/**
 * Validates an unknown error and returns it as a typed MSAL error if recoverable, otherwise throws a standard Error.
 * 
 * @param {unknown} error - The error to validate
 * @throws {Error} If the error is not an MSAL error or is not recoverable
 * @returns {MsalError} The validated MSAL error object with errorCode and errorMessage properties
 */
const validateMsalError = (error: unknown): MsalError => {
    if (!(error && typeof error === 'object' && 'errorCode' in error)) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown authentication error';
        throw new Error(`Authentication failed: ${errorMessage}`);
    }

    const msalError = error as { errorCode: string; errorMessage?: string };

    if (!isRecoverableError(msalError.errorCode)) {
        throw new Error(`Authentication failed: ${msalError.errorMessage ?? msalError.errorCode}`);
    }

    return msalError;
};

/**
 * Hook for managing authentication tokens
 */
export function useAuth() {
    const { instance, accounts } = useMsal();

    /**
     * Attempts to acquire an authentication token silently for the current user.
     * 
     * @param {boolean} forceRefresh - If true, forces a refresh of the token from the server instead of using a cached token.
     *   Use this when you suspect the cached token is expired or invalid.
     * @returns {Promise<string>} The access token string.
     * @throws {Error} If no authenticated accounts are found.
     * @throws {MsalError} MSAL error object if token acquisition fails (e.g., interaction_required, consent_required).
     */
    const acquireTokenSilently = useCallback(async (forceRefresh: boolean): Promise<string> => {
        if (accounts.length === 0) {
            throw new Error('No authenticated accounts found. Please sign in.');
        }

        const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
            forceRefresh,
        });

        console.log(`🔑 Token acquired (forceRefresh: ${forceRefresh}):`, !!response.accessToken);
        return response.accessToken;
    }, [instance, accounts]);

    /**
     * Attempts to acquire an authentication token through interactive popup authentication.
     * This method will show a popup window for the user to sign in.
     * 
     * @returns {Promise<string>} The access token string obtained through interactive authentication.
     * @throws {Error} If the interactive authentication fails, is cancelled by the user, or MSAL-specific errors occur during interactive authentication.
     */
    const acquireTokenInteractively = useCallback(async (): Promise<string> => {
        console.log('🔄 Attempting interactive token acquisition...');
        const response = await instance.acquireTokenPopup(loginRequest);
        console.log('🔑 Interactive token acquired:', !!response.accessToken);
        return response.accessToken;
    }, [instance]);

    /**
     * Handles token acquisition errors with a progressive fallback strategy.
     * First attempts force refresh, then falls back to interactive authentication if the error is recoverable.
     * 
     * @param {unknown} error - The error thrown during initial token acquisition.
     * @returns {Promise<string>} The access token string obtained through fallback methods.
     * @throws {Error} If the error is not recoverable or all fallback methods fail.
     */
    const handleTokenAcquisitionError = useCallback(async (error: unknown): Promise<string> => {
        console.error('Silent token acquisition failed:', error);

        // Validate that this is a recoverable MSAL error
        validateMsalError(error);

        // Try force refresh first
        try {
            console.log('🔄 Attempting force refresh...');
            return await acquireTokenSilently(true);
        } catch (refreshError) {
            console.error('⚠️ Force refresh failed...', refreshError);
            // Validate if the refreshError is recoverable before falling back
            validateMsalError(refreshError);
        }

        // Final fallback: Interactive authentication
        try {
            return await acquireTokenInteractively();
        } catch (interactiveError) {
            console.error('Interactive token acquisition also failed:', interactiveError);

            // Provide context-specific error messages based on the interactive error
            if (interactiveError && typeof interactiveError === 'object' && 'errorCode' in interactiveError) {
                const msalInteractiveError = interactiveError as { errorCode: string; errorMessage?: string };
                if (msalInteractiveError.errorCode === 'user_cancelled') {
                    throw new Error('Authentication was cancelled. Please try signing in again.');
                }
                if (msalInteractiveError.errorCode === 'popup_window_error') {
                    throw new Error('Authentication popup was blocked or closed. Please allow popups and try again.');
                }
                // Use the specific MSAL error message if available
                const errorMessage = msalInteractiveError.errorMessage || msalInteractiveError.errorCode;
                throw new Error(`Authentication failed: ${errorMessage}. Please try again.`);
            }

            // Fallback for non-MSAL errors
            const errorMessage = interactiveError instanceof Error ? interactiveError.message : 'Unknown error occurred';
            throw new Error(`Authentication failed: ${errorMessage}. Please try again.`);
        }
    }, [acquireTokenSilently, acquireTokenInteractively]);

    /**
     * Main function to get a valid access token for API calls.
     * Implements a progressive authentication strategy: silent token acquisition with fallback handling.
     * 
     * @returns {Promise<string | undefined>} The access token string, or undefined if in mock mode.
     * @throws {Error} If authentication fails after all retry attempts.
     */
    const getAccessToken = useCallback(async (): Promise<string | undefined> => {
        if (config.useMock) {
            return undefined;
        }

        console.log('🔄 Acquiring fresh access token...');

        try {
            return await acquireTokenSilently(false);
        } catch (error) {
            return await handleTokenAcquisitionError(error);
        }
    }, [acquireTokenSilently, handleTokenAcquisitionError]);

    return {
        getAccessToken,
    };
}
